import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  increment
} from 'firebase/firestore';

class WhatsAppService {
  constructor() {
    this.apiKey = null;
    this.apiUrl = null;
    this.provider = null; // 'twilio', 'whatsapp-business-api', 'custom', etc.
  }

  // Initialize with API credentials
  async initialize(coachingId) {
    try {
      const settingsRef = doc(db, 'coachingSettings', coachingId);
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        this.apiKey = settings.whatsappApiKey;
        this.apiUrl = settings.whatsappApiUrl;
        this.provider = settings.whatsappProvider || 'custom';
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error initializing WhatsApp service:', error);
      return false;
    }
  }

  // Save API configuration
  async saveConfiguration(coachingId, config) {
    try {
      const settingsRef = doc(db, 'coachingSettings', coachingId);
      await updateDoc(settingsRef, {
        whatsappApiKey: config.apiKey,
        whatsappApiUrl: config.apiUrl,
        whatsappProvider: config.provider,
        whatsappEnabled: true,
        updatedAt: serverTimestamp()
      });
      
      this.apiKey = config.apiKey;
      this.apiUrl = config.apiUrl;
      this.provider = config.provider;
      
      return { success: true };
    } catch (error) {
      console.error('Error saving WhatsApp configuration:', error);
      return { success: false, error: error.message };
    }
  }

  // Send WhatsApp message via API
  async sendMessage(phoneNumber, message, templateData = null) {
    if (!this.apiKey || !this.apiUrl) {
      throw new Error('WhatsApp service not configured');
    }

    try {
      // Format phone number (remove spaces, dashes, etc.)
      const formattedPhone = phoneNumber.replace(/[^\d+]/g, '');
      
      // Generic API call - adapt based on your provider
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          to: formattedPhone,
          message: message,
          template: templateData
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        return { success: true, messageId: result.messageId || result.id };
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      return { success: false, error: error.message };
    }
  }

  // Get students by various filters
  async getRecipients(coachingId, filters) {
    const recipients = [];
    
    try {
      console.log('=== GET RECIPIENTS DEBUG ===');
      console.log('CoachingId:', coachingId);
      console.log('Filters:', JSON.stringify(filters, null, 2));
      
      // Individual student
      if (filters.studentId) {
        try {
          console.log('Fetching individual student:', filters.studentId);
          const studentDoc = await getDoc(doc(db, 'students', filters.studentId));
          if (studentDoc.exists()) {
            const student = { id: studentDoc.id, ...studentDoc.data() };
            console.log('Student data:', student);
            if (student.phone) {
              recipients.push({
                id: student.id,
                name: student.name,
                phone: student.phone,
                email: student.email
              });
            } else {
              console.warn('Student has no phone number');
            }
          } else {
            console.warn('Student document does not exist');
          }
        } catch (err) {
          console.error('Error fetching individual student:', err);
        }
      }
      
      // Multiple specific students
      if (filters.studentIds && filters.studentIds.length > 0) {
        for (const studentId of filters.studentIds) {
          try {
            const studentDoc = await getDoc(doc(db, 'students', studentId));
            if (studentDoc.exists()) {
              const student = { id: studentDoc.id, ...studentDoc.data() };
              if (student.phone) {
                recipients.push({
                  id: student.id,
                  name: student.name,
                  phone: student.phone,
                  email: student.email
                });
              }
            }
          } catch (err) {
            console.error(`Error fetching student ${studentId}:`, err);
          }
        }
      }
      
      // All students from coaching
      if (filters.allStudents) {
        const studentsRef = collection(db, 'students');
        const studentsQuery = query(studentsRef, where('coachingId', '==', coachingId));
        const studentsSnapshot = await getDocs(studentsQuery);
        
        studentsSnapshot.docs.forEach(doc => {
          const student = { id: doc.id, ...doc.data() };
          if (student.phone) {
            recipients.push({
              id: student.id,
              name: student.name,
              phone: student.phone,
              email: student.email
            });
          }
        });
      }
      
      // Students from specific batch
      if (filters.batchId) {
        try {
          console.log('Fetching enrollments for batch:', filters.batchId);
          const enrollmentsRef = collection(db, 'batchEnrollments');
          // Query only by batchId, then filter by coachingId in code
          const enrollmentsQuery = query(
            enrollmentsRef, 
            where('batchId', '==', filters.batchId)
          );
          const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
          console.log('Found enrollments:', enrollmentsSnapshot.size);
          
          for (const enrollmentDoc of enrollmentsSnapshot.docs) {
            const enrollment = enrollmentDoc.data();
            
            // Filter by coachingId in code
            if (enrollment.coachingId !== coachingId) {
              console.log('Skipping - different coaching');
              continue;
            }
            
            console.log('Processing enrollment:', enrollment);
            
            // Try to get student from students collection first
            if (enrollment.studentId) {
              try {
                const studentDoc = await getDoc(doc(db, 'students', enrollment.studentId));
                if (studentDoc.exists()) {
                  const student = { id: studentDoc.id, ...studentDoc.data() };
                  console.log('Found student from students collection:', student.name, 'Phone:', student.phone);
                  if (student.phone) {
                    recipients.push({
                      id: student.id,
                      name: student.name,
                      phone: student.phone,
                      email: student.email,
                      batchId: filters.batchId
                    });
                  } else {
                    console.warn('Student has no phone:', student.name);
                  }
                } else {
                  console.warn('Student document not found:', enrollment.studentId);
                }
              } catch (err) {
                console.error(`Error fetching student ${enrollment.studentId}:`, err);
              }
            } 
            // If no studentId, check if enrollment has phone number directly
            else if (enrollment.phone) {
              console.log('Using phone from enrollment:', enrollment.name, enrollment.phone);
              recipients.push({
                id: enrollmentDoc.id,
                name: enrollment.name || 'Student',
                phone: enrollment.phone,
                email: enrollment.email,
                batchId: filters.batchId
              });
            }
            // If enrollment only has email, try to find user by email
            else if (enrollment.email) {
              console.log('Enrollment has email but no phone, checking users collection:', enrollment.email);
              try {
                const usersRef = collection(db, 'users');
                const usersQuery = query(usersRef, where('email', '==', enrollment.email.toLowerCase()));
                const usersSnapshot = await getDocs(usersQuery);
                
                if (!usersSnapshot.empty) {
                  const userData = usersSnapshot.docs[0].data();
                  if (userData.phone || userData.phoneNumber) {
                    console.log('Found phone in users collection:', userData.phone || userData.phoneNumber);
                    recipients.push({
                      id: usersSnapshot.docs[0].id,
                      name: enrollment.name || userData.displayName || userData.name || 'Student',
                      phone: userData.phone || userData.phoneNumber,
                      email: enrollment.email,
                      batchId: filters.batchId
                    });
                  } else {
                    console.warn('User found but has no phone number:', enrollment.email);
                  }
                } else {
                  console.warn('No user found with email:', enrollment.email);
                }
              } catch (err) {
                console.error('Error fetching user by email:', err);
              }
            } else {
              console.warn('Enrollment has no studentId, phone, or email:', enrollment);
            }
          }
        } catch (err) {
          console.error('Error fetching batch enrollments:', err);
        }
      }
      
      // Students from multiple batches
      if (filters.batchIds && filters.batchIds.length > 0) {
        console.log('=== FETCHING MULTIPLE BATCHES ===');
        console.log('Batch IDs:', filters.batchIds);
        
        for (const batchId of filters.batchIds) {
          try {
            console.log('Processing batch:', batchId);
            const enrollmentsRef = collection(db, 'batchEnrollments');
            // Query only by batchId
            const enrollmentsQuery = query(
              enrollmentsRef, 
              where('batchId', '==', batchId)
            );
            const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
            console.log(`Found ${enrollmentsSnapshot.size} enrollments for batch ${batchId}`);
            
            for (const enrollmentDoc of enrollmentsSnapshot.docs) {
              const enrollment = enrollmentDoc.data();
              console.log('Enrollment:', enrollment);
              
              // Filter by coachingId in code
              if (enrollment.coachingId !== coachingId) {
                console.log('Skipping - different coaching');
                continue;
              }
              
              if (enrollment.studentId) {
                try {
                  const studentDoc = await getDoc(doc(db, 'students', enrollment.studentId));
                  if (studentDoc.exists()) {
                    const student = { id: studentDoc.id, ...studentDoc.data() };
                    console.log('Student:', student.name, 'Phone:', student.phone);
                    if (student.phone && !recipients.find(r => r.id === student.id)) {
                      recipients.push({
                        id: student.id,
                        name: student.name,
                        phone: student.phone,
                        email: student.email,
                        batchId: batchId
                      });
                      console.log('Added student to recipients');
                    } else if (!student.phone) {
                      console.warn('Student has no phone');
                    } else {
                      console.log('Student already in recipients');
                    }
                  } else {
                    console.warn('Student document not found');
                  }
                } catch (err) {
                  console.error(`Error fetching student ${enrollment.studentId}:`, err);
                }
              } else if (enrollment.phone && !recipients.find(r => r.phone === enrollment.phone)) {
                console.log('Using phone from enrollment:', enrollment.phone);
                recipients.push({
                  id: enrollmentDoc.id,
                  name: enrollment.name || 'Student',
                  phone: enrollment.phone,
                  email: enrollment.email,
                  batchId: batchId
                });
              } else {
                console.warn('No studentId or phone in enrollment');
              }
            }
            console.log(`Recipients after batch ${batchId}:`, recipients.length);
          } catch (err) {
            console.error(`Error fetching enrollments for batch ${batchId}:`, err);
          }
        }
        console.log('Total recipients from multiple batches:', recipients.length);
      }
      
      // Custom phone numbers (for marketing)
      if (filters.customPhones && filters.customPhones.length > 0) {
        filters.customPhones.forEach((phone, index) => {
          if (phone && phone.trim()) {
            recipients.push({
              id: `custom_${index}`,
              name: 'Custom Contact',
              phone: phone.trim(),
              isCustom: true
            });
          }
        });
      }
      
      // Remove duplicates based on phone number
      const uniqueRecipients = recipients.filter((recipient, index, self) =>
        index === self.findIndex((r) => r.phone === recipient.phone)
      );
      
      console.log(`Found ${uniqueRecipients.length} unique recipients`);
      return uniqueRecipients;
    } catch (error) {
      console.error('Error getting recipients:', error);
      throw error;
    }
  }

  // Send bulk messages
  async sendBulkMessages(coachingId, messageData) {
    const {
      recipients,
      message,
      messageType,
      metadata = {}
    } = messageData;

    const results = {
      total: recipients.length,
      successful: 0,
      failed: 0,
      errors: []
    };

    // Create message log entry
    const messageLogRef = await addDoc(collection(db, 'whatsappMessages'), {
      coachingId,
      messageType,
      message,
      totalRecipients: recipients.length,
      sentAt: serverTimestamp(),
      status: 'sending',
      metadata
    });

    // Send messages
    for (const recipient of recipients) {
      try {
        // Use personalized message if available, otherwise use default
        const messageToSend = recipient.personalizedMessage || message;
        
        const result = await this.sendMessage(recipient.phone, messageToSend);
        
        if (result.success) {
          results.successful++;
          
          // Log individual message
          await addDoc(collection(db, 'whatsappMessageLogs'), {
            messageLogId: messageLogRef.id,
            coachingId,
            recipientId: recipient.id,
            recipientName: recipient.name,
            recipientPhone: recipient.phone,
            message: messageToSend,
            messageType,
            status: 'sent',
            sentAt: serverTimestamp(),
            messageId: result.messageId,
            metadata
          });
        } else {
          results.failed++;
          results.errors.push({
            recipient: recipient.name,
            phone: recipient.phone,
            error: result.error
          });
          
          // Log failed message
          await addDoc(collection(db, 'whatsappMessageLogs'), {
            messageLogId: messageLogRef.id,
            coachingId,
            recipientId: recipient.id,
            recipientName: recipient.name,
            recipientPhone: recipient.phone,
            message: messageToSend,
            messageType,
            status: 'failed',
            error: result.error,
            sentAt: serverTimestamp(),
            metadata
          });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          recipient: recipient.name,
          phone: recipient.phone,
          error: error.message
        });
      }
    }

    // Update message log with final status
    await updateDoc(doc(db, 'whatsappMessages', messageLogRef.id), {
      status: 'completed',
      successfulCount: results.successful,
      failedCount: results.failed,
      completedAt: serverTimestamp()
    });

    // Update monthly statistics
    await this.updateMonthlyStats(coachingId, messageType, results.successful);

    return results;
  }

  // Update monthly statistics
  async updateMonthlyStats(coachingId, messageType, count) {
    try {
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      const statsRef = doc(db, 'whatsappMonthlyStats', `${coachingId}_${monthKey}`);
      const statsDoc = await getDoc(statsRef);
      
      if (statsDoc.exists()) {
        await updateDoc(statsRef, {
          totalMessages: increment(count),
          [`messageTypes.${messageType}`]: increment(count),
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'whatsappMonthlyStats'), {
          id: `${coachingId}_${monthKey}`,
          coachingId,
          month: monthKey,
          year: now.getFullYear(),
          monthNumber: now.getMonth() + 1,
          totalMessages: count,
          messageTypes: {
            [messageType]: count
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating monthly stats:', error);
    }
  }

  // Get monthly statistics
  async getMonthlyStats(coachingId, year, month) {
    try {
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;
      const statsRef = collection(db, 'whatsappMonthlyStats');
      const statsQuery = query(
        statsRef,
        where('coachingId', '==', coachingId),
        where('month', '==', monthKey)
      );
      
      const statsSnapshot = await getDocs(statsQuery);
      
      if (!statsSnapshot.empty) {
        return statsSnapshot.docs[0].data();
      }
      
      return {
        totalMessages: 0,
        messageTypes: {}
      };
    } catch (error) {
      console.error('Error getting monthly stats:', error);
      return null;
    }
  }

  // Get message history
  async getMessageHistory(coachingId, filters = {}) {
    try {
      const messagesRef = collection(db, 'whatsappMessages');
      let messagesQuery = query(messagesRef, where('coachingId', '==', coachingId));
      
      if (filters.messageType) {
        messagesQuery = query(messagesQuery, where('messageType', '==', filters.messageType));
      }
      
      const messagesSnapshot = await getDocs(messagesQuery);
      
      return messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting message history:', error);
      return [];
    }
  }

  // Get message templates
  getMessageTemplates() {
    return {
      class: {
        name: 'Class Notification',
        template: 'Dear {studentName},\n\nClass scheduled for {subject} on {date} at {time}.\n\nVenue: {venue}\nTopic: {topic}\n\nPlease be on time.\n\nRegards,\n{coachingName}'
      },
      announcement: {
        name: 'Announcement',
        template: 'Dear {studentName},\n\n{announcement}\n\nRegards,\n{coachingName}'
      },
      testResult: {
        name: 'Test Result',
        template: 'Dear {studentName},\n\nYour test results for {testName}:\n\nScore: {score}/{totalMarks}\nPercentage: {percentage}%\nRank: {rank}\n\nKeep up the good work!\n\nRegards,\n{coachingName}'
      },
      homework: {
        name: 'Homework Reminder',
        template: 'Dear {studentName},\n\nHomework assigned for {subject}:\n\n{homeworkDetails}\n\nDue Date: {dueDate}\n\nRegards,\n{coachingName}'
      },
      custom: {
        name: 'Custom Message',
        template: '{message}'
      },
      marketing: {
        name: 'Marketing Message',
        template: '{message}'
      }
    };
  }
}

export default new WhatsAppService();
